import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const STORAGE_KEY = 'synoti_theme';

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return { mode: 'dark', primaryColor: '#7c3aed', accentColor: '#06b6d4', logo: '', density: 'comfortable' };
  });

  const applyTheme = useCallback((t) => {
    const root = document.documentElement;
    const isDark = t.mode === 'dark' || (t.mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    // Legacy Tailwind pages key off .dark class; new design CSS keys off [data-theme].
    root.classList.toggle('dark', isDark);
    root.setAttribute('data-theme', isDark ? 'dark' : 'light');
    root.style.setProperty('--primary', t.primaryColor || '#7c3aed');
    root.style.setProperty('--accent', t.accentColor || '#06b6d4');
  }, []);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  }, [theme, applyTheme]);

  useEffect(() => {
    if (theme.mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme(theme);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme, applyTheme]);

  const updateTheme = useCallback((updates) => {
    setTheme(prev => ({ ...prev, ...updates }));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, updateTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
